import { forwardRef, useEffect, useRef } from "react";
import { Input, type InputProps } from "@/components/ui/input";
import { useFieldId } from "@/components/ui/field-context";
import type { GooglePlace } from "@mtform/shared";
import { useGoogleMaps } from "@/hooks/useGoogleMaps";

export interface AddressFieldProps
  extends Omit<InputProps, "value" | "onChange" | "autoComplete"> {
  value: string;
  onChange: (value: string) => void;
  /** Setter for the paired city/state/zip field — wired via the Place result. */
  onPlace?: (place: GooglePlace, cityZip: string) => void;
  /** Called when the user edits the address after a Place was set; consumers
   *  drop the cached place so we don't ship mismatched coords. Also fired
   *  when the trailing X is clicked. */
  onClearPlace?: () => void;
}

const ADDRESS_ERROR_PATTERN = /oops!\s*something went wrong\.?/iu;

export const AddressField = forwardRef<HTMLInputElement, AddressFieldProps>(
  (
    {
      value,
      onChange,
      onPlace,
      onClearPlace,
      placeholder = "Start typing address…",
      clearable = true,
      id,
      ...props
    },
    forwardedRef,
  ) => {
    const localRef = useRef<HTMLInputElement | null>(null);
    const { google, unavailable } = useGoogleMaps();
    // Adopt the surrounding <Field>'s id so the visible label associates with
    // this input even when the call site passed no explicit id. An explicit
    // `id` prop still wins.
    const fieldId = useFieldId();
    const lastValueRef = useRef("");

    // Pass the ref both to consumers (RHF) and to our local for autocomplete wiring.
    const setRef = (node: HTMLInputElement | null) => {
      localRef.current = node;
      if (typeof forwardedRef === "function") forwardedRef(node);
      else if (forwardedRef) (forwardedRef as React.MutableRefObject<HTMLInputElement | null>).current = node;
    };

    // Hold the latest onPlace in a ref so the autocomplete effect below
    // can stay a one-time-mount-and-forget — without this, every parent
    // re-render hands us a new inline arrow, the effect tears down the
    // autocomplete + listener, and Google's place_changed event fires
    // *into the void* between the input change and the listener being
    // re-attached. Symptom: typing/picking an address never reaches
    // onPlace, the pin never pans. Drag still works because that
    // listener is on the marker, not the autocomplete instance.
    const onPlaceRef = useRef(onPlace);
    onPlaceRef.current = onPlace;

    useEffect(() => {
      if (!google || !localRef.current) return;
      const input = localRef.current;
      let autocomplete: google.maps.places.Autocomplete;
      try {
        autocomplete = new google.maps.places.Autocomplete(input, {
          fields: ["address_components", "formatted_address", "geometry"],
          componentRestrictions: { country: "us" },
          types: ["address"],
        });
      } catch (err) {
        console.warn("[AddressField] autocomplete init failed:", err);
        return;
      }
      const listener = autocomplete.addListener("place_changed", () => {
        let place: google.maps.places.PlaceResult;
        try {
          place = autocomplete.getPlace();
        } catch {
          return;
        }
        const components = place.address_components ?? [];
        const fa = String(place.formatted_address ?? "").toLowerCase();
        if (fa.includes("oops") || fa.includes("wrong")) return;

        const get = (type: string) =>
          components.find((c) => c.types.includes(type))?.short_name ?? "";

        const city = get("locality") || get("sublocality");
        const state = get("administrative_area_level_1");
        const postalCode = get("postal_code");
        const cityZip = [
          [city, state].filter(Boolean).join(", "),
          postalCode,
        ]
          .filter(Boolean)
          .join(" ");

        // Google's PlaceResult has `geometry.location` as a LatLng with
        // lat() / lng() methods. The newer Place class (which the script
        // sometimes hands us depending on which `loading=` mode is
        // active) returns `location` at the top level with lat / lng as
        // plain number properties. Try the legacy nesting first, fall
        // back to the top-level location, and finally log the raw place
        // shape if neither yielded a valid pair so we can see what
        // Google actually returned. */
        const placeAny = place as unknown as { location?: unknown };
        const ll =
          extractLatLng(place.geometry?.location) ??
          extractLatLng(placeAny.location);
        if (!ll) {
          console.warn(
            "[AddressField] couldn't read lat/lng from place — pin won't pan automatically. Drop the pin manually or paste coords. Raw place:",
            place,
          );
        }

        const placeResult: GooglePlace = {
          lat: ll?.lat ?? null,
          lon: ll?.lng ?? null,
          buildingNumber: get("street_number"),
          line1: get("route"),
          subpremise: get("subpremise"),
          city,
          state,
          postalCode,
          formatted: place.formatted_address ?? "",
        };
        onPlaceRef.current?.(placeResult, cityZip);
      });
      return () => {
        google.maps.event.removeListener(listener);
      };
      // Intentional: only re-run when google flips from null to loaded.
      // onPlace lives on the ref so we don't tear the autocomplete
      // down on every parent re-render.
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [google]);

    return (
      <Input
        ref={setRef}
        id={id ?? fieldId}
        type="text"
        autoComplete="street-address"
        placeholder={unavailable ? "Type address (autocomplete unavailable)" : placeholder}
        value={value}
        clearable={clearable}
        onClear={() => {
          onChange("");
          onClearPlace?.();
          lastValueRef.current = "";
        }}
        onChange={(e) => {
          const next = e.target.value;
          if (ADDRESS_ERROR_PATTERN.test(next)) {
            onChange(lastValueRef.current);
            return;
          }
          lastValueRef.current = next;
          onChange(next);
          onClearPlace?.();
        }}
        {...props}
      />
    );
  },
);
AddressField.displayName = "AddressField";

/** Pull a `{ lat, lng }` pair out of whatever Google returned, tolerating
 *  both the legacy `LatLng` (lat() / lng() methods) and the newer Place
 *  shape (lat / lng plain number properties). Returns null if the value
 *  isn't usable. */
function extractLatLng(loc: unknown): { lat: number; lng: number } | null {
  if (!loc || typeof loc !== "object") return null;
  const obj = loc as Record<string, unknown>;
  let lat: unknown = obj.lat;
  let lng: unknown = obj.lng;
  if (typeof lat === "function") {
    try {
      lat = (lat as () => unknown)();
    } catch {
      lat = undefined;
    }
  }
  if (typeof lng === "function") {
    try {
      lng = (lng as () => unknown)();
    } catch {
      lng = undefined;
    }
  }
  if (
    typeof lat === "number" &&
    typeof lng === "number" &&
    Number.isFinite(lat) &&
    Number.isFinite(lng)
  ) {
    return { lat, lng };
  }
  return null;
}
